import { addHours } from "date-fns";
import {
  SchedulerOrganizationalUnitsQuery,
  VisitEventStatusEnum,
  VisitResourceVirtualProviderStatusEnum as VStatusEnum
} from "~/graphql/__generated__/graphql";

import { OrgConfig } from "~/providers/OrgConfigProvider";

export const UNASSIGNED_PROVIDER_ID = -1;
export const UNASSIGNED_PROVIDER_TYPE_ID = -1;

export const getSchedulingOrganizationalUnitId = (
  episodeOrganizationalUnitId: number | null | undefined,
  organizationalUnits: Pick<SchedulerOrganizationalUnitsQuery["organizationalUnit"][number], "id">[],
  selectedOrganizationalUnitId?: number,
  autoSelectSingleUnit = true,
) => {
  if (episodeOrganizationalUnitId != null) return episodeOrganizationalUnitId;
  if (autoSelectSingleUnit && organizationalUnits.length === 1) return organizationalUnits[0].id;
  return organizationalUnits.find((unit) => unit.id === selectedOrganizationalUnitId)?.id;
};

export const orJoin = (items: string[]) => {
  items = items.filter(Boolean)

  if (items.length < 2 ) { return items.join("") }

  return items.slice(0, -1).join(", ") + " or " + items[items.length - 1]
}

export const andJoin = (items: string[]) => {
  items = items.filter(Boolean)

  if (items.length < 2 ) { return items.join("").trim() }

  return items.slice(0, -1).join(", ") + " and " + items[items.length - 1].trim()
}

export const formatProvidersNames = (visitResources: {
  provider?: { firstName: string | null, lastName: string | null } | null
}[]) => {
  return visitResources.reduce((acc, resource, index) => {
    const provider = resource.provider;
    if (provider?.firstName) {
      const fullName = `${provider?.firstName} ${provider?.lastName}`;
      let separator = "" ;
      if (index > 0) {
        separator = index === (visitResources.length - 1) ? " and " : ', ';
      }
      acc = `${acc}${separator} ${fullName}`;
    }
    return acc;
  }, "")
}

export const calculateDuration = (
    orgConfig: OrgConfig,
    visitTypeId: number | undefined,
    selectedServices: string[]
  ): string => {
  const selectedVisitType = visitTypeId && orgConfig.visitTypes[visitTypeId];
  if (!selectedVisitType) {
    return "0";
  }
  const visitRequirementDurations = Object.values(orgConfig.visitResourceRequirements)
    .filter(vrr => vrr !== undefined)
    .filter(vrr => vrr.visitTypeId === visitTypeId)
    .map(vrr => vrr.duration + vrr.offset) || [];

  const servicesDurations =
    selectedVisitType?.visitTypeServices
      ?.filter(({ service }) =>
        selectedServices.includes(service?.id?.toString() || "")
      )
      ?.map(({ service }) => service?.duration || 0) || [];
  const serviceTotalDuration =
    servicesDurations?.reduce((a, b) => a + b, 0) || 0;

  const duration =
    Math.max(...visitRequirementDurations, 0) + serviceTotalDuration;

  return duration.toString();
};

export const getServiceDefaults = (orgConfig: OrgConfig, visitTypeId: number): string[] => {
  if (!visitTypeId || !orgConfig) {
    return [];
  }
  const selectedVisitType = orgConfig.visitTypes[visitTypeId];
  const defaultVisitTypeServices =
    selectedVisitType?.visitTypeServices?.filter(
      (vts) => vts.selectedByDefault
    );

  const newDefaultServices =
    defaultVisitTypeServices
      ?.map(({ service }) => service?.id?.toString() || "")
      .filter((s) => s) || [];

  return newDefaultServices;
};

export const getVisitWarningSeverityStyles = (severity: number) => {
  switch (severity) {
    case 3:
      return { backgroundColor: '#ffc1c1', color: '#6f3928' };
    case 2:
      return { backgroundColor: '#FFECB3', color: '#5D4037' };
    case 1:
      return { backgroundColor: '#FFF8E1', color: '#5D4037' };
    default:
      return { backgroundColor: '#FFF8E1', color: '#5D4037' };
  }
};

export type PartialVisit = {
  id: number;
  careRequirementId?: number | null;
  eventStatus?: VisitEventStatusEnum;
  manualEdits?: boolean;
  startTime?: string;
  visitResources?: {
    id: number;
    virtualProviderStatus?: VStatusEnum | null;
    resourceRequirement?: {
      invitedToVisit: boolean;
    } | null | undefined;
    providerId: number | null | undefined;
  }[]
  episode?: {
    serviceLineId?: number
  } | null
}

type NextVirtualStatusTransition = {
  label: string,
  disallowReason?: string,
  nextVirtualStatus?: { id: number, status: VStatusEnum },
} | undefined;

export const visitResourceNeedsAttention = (vr: NonNullable<PartialVisit["visitResources"]>[number], providerId: number | null | undefined) => {
  if (!providerId || providerId !== vr.providerId) { return false; }
  return vr.virtualProviderStatus === VStatusEnum.Notified;
}

export const nextVirtualStatusTransition = (visit: PartialVisit, providerId: number | undefined | null): NextVirtualStatusTransition => {
  const visitResource = visit.visitResources?.find(vr => vr.providerId === providerId);
  if (!providerId || !visitResource?.resourceRequirement?.invitedToVisit) { return; }

  if (visitResource.virtualProviderStatus === VStatusEnum.Complete) {
    return {
      label: "Call Complete",
      disallowReason: "Call already completed",
    };
  }

  const maxFutureHours = 4;
  if(!visit.startTime || new Date(visit.startTime) > addHours(new Date(), maxFutureHours)) {
    return {
      label: "Joining Call",
      disallowReason: `Visit starts more than ${maxFutureHours} hours in the future`,
    };
  }

  return {
    label: visitResource.virtualProviderStatus === VStatusEnum.Joined ? "Call Complete" : "Joining Call",
    nextVirtualStatus: {
      id: visitResource.id,
      status: visitResource.virtualProviderStatus === VStatusEnum.Joined ? VStatusEnum.Complete : VStatusEnum.Joined,
    },
  };
}

export const getEpisodeGroupLabel = (group: number) => `Group ${String.fromCharCode(64 + group)}`;
